import { KeyboardEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Keyboard, Package, PackageMinus, PackagePlus } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from 'utils/classnames';
import { Loader } from 'components/ui/Loader';
import {
  CARTON_BUILD_OPERATION_TYPES,
  CARTON_LICENSE_STATUS_TYPES,
  DISPOSITION_COLORS,
  SCREEN_TYPES,
} from 'utils/constants';
import { useAppStore } from 'store';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { useKeyboardInputToggle } from 'hooks/useKeyboardInputToggle';
import { usePalletBuildStore } from 'store/palletBuildStore';
import { useAuthStore } from 'store/authStore';
import { buildCarton } from 'services/api/palletBuild';
import { playCallbackSound, playErrorSound, playGoodSound } from 'utils/sounds';
import { isValidUpc } from 'utils/validation';
import { translateProblemMessage } from 'utils/problemMessages';
import { PalletBuildBackButton } from './PalletBuildBackButton';

export const AssignProductsPage = () => {
  const { t } = useTranslation();
  const [scanValue, setScanValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { keyboardVisible, toggleKeyboardInput } = useKeyboardInputToggle(inputRef);
  const { setCurrentScreen } = useAppStore();
  const { cartonId, carton, setCarton, setCartonId } = usePalletBuildStore();
  const [mode, setMode] = useState<'add' | 'remove'>('add');
  const [loading, setLoading] = useState(false);

  const isRemoveMode = mode === 'remove';
  const products = carton?.cartonDetail ?? [];

  const handleSubmit = async () => {
    const trimmed = scanValue.trim();
    if (!trimmed) return;
    setScanValue('');
    setHasError(false);

    const failureMessage = t('assignProducts.actionFailed', {
      action: t(isRemoveMode ? 'assignProducts.removeAction' : 'assignProducts.addAction'),
    });

    if (!isValidUpc(trimmed)) {
      setErrorMessage(t('assignProducts.upcInvalid'));
      setHasError(true);
      playErrorSound();
      return;
    }

    setLoading(true);
    try {
      const user_id = useAuthStore.getState().user?.id ?? '';
      const result = await buildCarton({
        operation_id: isRemoveMode
          ? CARTON_BUILD_OPERATION_TYPES.REMOVE
          : CARTON_BUILD_OPERATION_TYPES.ASSIGN,
        user_id,
        license_id: cartonId!,
        upc: trimmed,
      });
      if (!result || result.responseState !== 'confirmed') {
        setErrorMessage(translateProblemMessage(result?.responseMsg) || failureMessage);
        setHasError(true);
        playErrorSound();
        return;
      }
      setCarton(result);
      isRemoveMode ? playCallbackSound() : playGoodSound();
      toast.success(
        isRemoveMode ? t('assignProducts.productRemoved') : t('assignProducts.productAdded')
      );
    } catch {
      setErrorMessage(failureMessage);
      setHasError(true);
      playErrorSound();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleDone = () => {
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD);
  };

  const handleClearCarton = () => {
    setCartonId(null);
    setCurrentScreen(SCREEN_TYPES.PALLET_BUILD_CASE_SCAN);
  };

  const isCartonClosed = carton.license_status === CARTON_LICENSE_STATUS_TYPES.CLOSED;

  return (
    <div
      className={cn(
        '-mx-4 -mb-6 flex min-h-full flex-col gap-4 px-6 pb-6 pt-4',
        isRemoveMode || hasError
          ? DISPOSITION_COLORS.PROBLEM.gradient
          : isCartonClosed
            ? DISPOSITION_COLORS.EXCEPTION.gradient
            : DISPOSITION_COLORS.CONFIRMING.gradient
      )}
    >
      <div className="relative flex flex-col items-center gap-1">
        <PalletBuildBackButton onClick={handleDone} light />
        <span className="text-xs font-semibold uppercase tracking-widest text-white/80">
          {t('assignProducts.eyebrow')}
        </span>
        <h2 className="text-2xl font-bold text-white">{t('assignProducts.title')}</h2>

        {isCartonClosed ? (
          <p className="text-3xl font-bold text-white text-center">
            {t('assignProducts.cartonClosedMessage')}
          </p>
        ) : (
          hasError && <p className="text-3xl font-bold text-white text-center">{errorMessage}</p>
        )}
      </div>

      <div className="flex items-center gap-3 rounded-xl bg-emerald-50 px-4 py-3">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-100">
          <Package className="h-4 w-4 text-emerald-600" />
        </div>
        <div className="flex flex-col min-w-0 flex-1">
          <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">
            {t('assignProducts.caseLabel')}
          </span>
          <span className="font-mono text-sm font-bold text-slate-800 truncate">
            {cartonId ?? '-'}
          </span>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={handleClearCarton}
          className="h-8 shrink-0 rounded-lg px-3 text-xs font-bold"
        >
          {t('assignProducts.clear')}
        </Button>
      </div>

      <div
        className={cn(
          'flex items-center gap-3 rounded-xl px-4 py-3',
          isRemoveMode ? 'bg-rose-50' : 'bg-emerald-50'
        )}
      >
        <div
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg',
            isRemoveMode ? 'bg-rose-100' : 'bg-emerald-100'
          )}
        >
          {isRemoveMode ? (
            <PackageMinus className="h-4 w-4 text-rose-600" />
          ) : (
            <PackagePlus className="h-4 w-4 text-emerald-600" />
          )}
        </div>
        <span
          className={cn(
            'text-xl font-bold uppercase tracking-widest',
            isRemoveMode ? 'text-rose-600' : 'text-emerald-600'
          )}
        >
          {isRemoveMode
            ? t('assignProducts.removeProductHeading')
            : t('assignProducts.addProductHeading')}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t('assignProducts.productHeader')}
          </span>
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">
            {t('assignProducts.qtyHeader')}
          </span>
        </div>

        {products.length === 0 ? (
          <div className="flex flex-col items-center gap-1 px-4 py-8">
            <Package className="h-8 w-8 text-slate-200" />
            <p className="text-sm text-slate-400">{t('assignProducts.noProductsYet')}</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {products.map(({ product_qty, upc }) => (
              <div key={upc} className="flex items-center justify-between px-4 py-3">
                <span className="font-mono text-sm font-medium text-slate-700">{upc}</span>
                <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-bold text-emerald-700">
                  {product_qty}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <Loader
          variant="inline"
          className="rounded-xl bg-slate-50"
          text={
            isRemoveMode ? t('assignProducts.removingProduct') : t('assignProducts.addingProduct')
          }
        />
      )}

      <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
        <Label className="mb-2 block text-sm font-semibold text-slate-600">
          {t('assignProducts.productBarcodeLabel')}
        </Label>
        <div className="flex items-center gap-2">
          <Input
            ref={inputRef}
            type="text"
            inputMode={keyboardVisible ? 'text' : 'none'}
            placeholder={t('assignProducts.placeholder')}
            value={scanValue}
            onChange={(e) => setScanValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => requestAnimationFrame(() => inputRef.current?.focus())}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            className="text-base"
            disabled={isCartonClosed}
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={toggleKeyboardInput}
            aria-pressed={keyboardVisible}
            className="h-12 w-12 shrink-0 rounded-xl border-slate-300"
            disabled={isCartonClosed}
          >
            <Keyboard className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="buttons-container !bg-transparent">
        {!isCartonClosed && (
          <Button
            variant="outline"
            onClick={() => setMode(isRemoveMode ? 'add' : 'remove')}
            className={cn('flex-1 h-12', {
              'text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700': isRemoveMode,
              'text-rose-600 hover:bg-rose-50 hover:text-rose-700': !isRemoveMode,
            })}
          >
            {t('assignProducts.switchTo', {
              mode: t(isRemoveMode ? 'assignProducts.addLabel' : 'assignProducts.removeLabel'),
            })}
          </Button>
        )}

        <Button
          onClick={handleDone}
          className="bg-emerald-600 text-white hover:bg-emerald-700 flex-1 h-12"
        >
          {t('assignProducts.done')}
        </Button>
      </div>
    </div>
  );
};
